<template>
    <div>
        <div class="mb-3">
            <span class="item-detail-title">{{ $t('common.time_range') }}</span>
            <div class="row g-2">
                <div class="col-sm-6">
                    <input
                        v-model="row.start_date"
                        type="date"
                        class="form-control form-control-sm"
                    >
                </div>
                <div class="col-sm-6">
                    <input
                        v-model="row.end_date"
                        type="date"
                        class="form-control form-control-sm"
                    >
                </div>
            </div>
            <div
                v-if="validationErrors.timeRange"
                class="text-danger small mt-1"
            >
                {{ validationErrors.timeRange }}
            </div>
        </div>

        <div class="mb-3">
            <open-contract-filter
                ref="contracts"
                :from="row.start_date"
                :to="row.end_date"
                :show-title="true"
                :show-company="false"
                :apply-padding="false"
                :required="!selectedContract"
                @contract-selected="setContract"
                @loading="fetchingContracts = true"
                @loading-completed="fetchingContracts = false"
            />
            <div
                v-if="validationErrors.contract"
                class="text-danger small mt-1"
            >
                {{ validationErrors.contract }}
            </div>
        </div>

        <div
            v-if="selectedContract && attachmentsError"
            class="alert alert-danger py-2 px-3"
        >
            {{ $t('contract_sanction.attachments_load_error') }}
        </div>

        <div class="mb-3">
            <span class="item-detail-title">{{ $t('common.info') }}</span>
            <textarea
                v-model="row.info"
                rows="3"
                maxlength="500"
                class="form-control form-control-sm"
                :disabled="!selectedContract"
            />
        </div>

        <div
            v-if="optionsError"
            class="alert alert-danger py-2 px-3"
        >
            {{ $t('contract_sanction.defaults_load_error') }}
        </div>

        <div class="row g-2 mb-3">
            <div class="col-sm-6">
                <span class="item-detail-title">{{ $t('account.account') }}</span>
                <div
                    v-if="!optionsError && sanctionAccounts.length === 0"
                    class="text-danger small"
                >
                    {{ $t('contract_sanction.account_not_configured') }}
                </div>
                <multiselect
                    v-else-if="accountNeedsSelection"
                    v-model="selectedAccount"
                    :options="sanctionAccounts"
                    label="name"
                    track-by="id"
                    :custom-label="accountLabel"
                    :searchable="true"
                    :selected-label="$t('common.selected')"
                    :select-label="$t('common.select')"
                    :deselect-label="$t('common.remove_selection')"
                    :placeholder="$t('contract_sanction.select_account')"
                    :class="{required_field : accountNeedsSelection && !selectedAccount}"
                />
                <input
                    v-else
                    type="text"
                    class="form-control form-control-sm"
                    :value="singleAccount ? accountLabel(singleAccount) : '-'"
                    disabled
                    readonly
                >
                <div
                    v-if="validationErrors.account"
                    class="text-danger small mt-1"
                >
                    {{ validationErrors.account }}
                </div>
            </div>
            <div class="col-sm-6">
                <span class="item-detail-title">{{ $t('account.invoicing_code') }}</span>
                <div
                    v-if="!optionsError && sanctionCodes.length === 0"
                    class="text-danger small"
                >
                    {{ $t('contract_sanction.code_not_configured') }}
                </div>
                <multiselect
                    v-else-if="codeNeedsSelection"
                    v-model="selectedCode"
                    :options="sanctionCodes"
                    label="name"
                    track-by="id"
                    :custom-label="codeLabel"
                    :searchable="true"
                    :selected-label="$t('common.selected')"
                    :select-label="$t('common.select')"
                    :deselect-label="$t('common.remove_selection')"
                    :placeholder="$t('contract_sanction.select_code')"
                    :class="{required_field : codeNeedsSelection && !selectedCode}"
                />
                <input
                    v-else
                    type="text"
                    class="form-control form-control-sm"
                    :value="singleCode ? codeLabel(singleCode) : '-'"
                    disabled
                    readonly
                >
                <div
                    v-if="validationErrors.code"
                    class="text-danger small mt-1"
                >
                    {{ validationErrors.code }}
                </div>
            </div>
        </div>

        <div class="row g-2 mb-3">
            <div class="col-sm-3">
                <span class="item-detail-title">{{ $t('trip_list.unit_price') }}</span>
                <input
                    v-model.number="row.unit_price"
                    type="number"
                    class="form-control form-control-sm text-end"
                    :max="-0.01"
                    step="0.01"
                    :disabled="!selectedContract"
                >
            </div>
            <div class="col-sm-3">
                <span class="item-detail-title">{{ $t('material_reports.unit') }}</span>
                <input
                    type="text"
                    class="form-control form-control-sm"
                    :value="pricingModelToText({unit1: 3})"
                    disabled
                    readonly
                >
            </div>
            <div class="col-sm-3">
                <span class="item-detail-title">{{ $t('credit_note.original_sum') }}</span>
                <input
                    type="text"
                    class="form-control form-control-sm text-end"
                    value="1"
                    disabled
                    readonly
                >
            </div>
            <div class="col-sm-3">
                <span class="item-detail-title">{{ $t('credit_note.total') }}</span>
                <input
                    type="text"
                    class="form-control form-control-sm text-end"
                    :value="formatCurrency(total)"
                    disabled
                    readonly
                >
            </div>
            <div
                v-if="validationErrors.unitPrice"
                class="text-danger small mt-1"
            >
                {{ validationErrors.unitPrice }}
            </div>
        </div>

        <div v-if="selectedContract" class="mb-3 d-flex justify-content-between align-items-start">
            <span class="item-detail-title">{{ $t('contracts.attachments') }}</span>
            <span v-if="contractAttachments.length === 0" class="text-muted small">{{ $t('daily_summary.no_contract_attachments') }}</span>
            <invoice-attachment-selector
                v-else
                v-model="selectedAttachments"
                :contract-attachments="contractAttachments"
                :contract-id="selectedContract.id"
                @downloadAttachment="downloadAttachment"
            />
        </div>
        <div
            v-if="validationErrors.attachments"
            class="text-danger small mt-1"
        >
            {{ validationErrors.attachments }}
        </div>

        <div class="button-container d-flex justify-content-end mt-3">
            <button
                class="btn btn-secondary btn-sm me-2"
                @click="$emit('close')"
            >
                {{ $t('common.cancel') }}
            </button>
            <button
                class="btn btn-success btn-sm"
                :disabled="submitting || creationBlocked"
                @click="initiateSubmit"
            >
                <span
                    v-if="submitting"
                    class="spinner-border spinner-border-sm me-1"
                />
                {{ $t('common.save') }}
            </button>
        </div>

        <base-modal
            v-if="showConfirm"
            hide-header
            hide-footer
            @close="showConfirm = false"
        >
            <div class="contract-sanction-confirm-header mb-3">
                {{ $t('contract_sanction.confirm_title') }}
            </div>
            <p>{{ $t('contract_sanction.confirm_body') }}</p>
            <div class="d-flex justify-content-end mt-3">
                <button
                    class="btn btn-secondary btn-sm me-2"
                    @click="showConfirm = false"
                >
                    {{ $t('contract_sanction.cancel_button') }}
                </button>
                <button
                    class="btn btn-success btn-sm"
                    :disabled="submitting"
                    @click="confirmSubmit"
                >
                    <span
                        v-if="submitting"
                        class="spinner-border spinner-border-sm me-1"
                    />
                    {{ $t('contract_sanction.confirm_button') }}
                </button>
            </div>
        </base-modal>

        <teleport to="body">
            <div
                v-if="loading"
                id="loader"
                class="spinner"
                style="z-index: 9999;"
            />
        </teleport>
    </div>
</template>

<script>
import { restApi } from '../mixins/RestApiMixin'
import { timeUtils } from '../mixins/TimeUtils'
import { inject } from 'vue'
import { priceHelper } from '../mixins/PricingMixin.js'
import { invoicingHelper } from '../mixins/InvoicingMixin.js'
import { attachmentHelper } from '../mixins/AttachmentMixin.js'
import { downloadHelper } from '../mixins/DownloadMixin'
import InvoiceAttachmentSelector from './InvoiceAttachmentSelector.vue'
import BaseModal from '../BaseModal.vue'
import OpenContractFilter from '../contract/OpenContractFilter.vue'

export default {
    name: 'ContractSanctionEditor',
    components: { InvoiceAttachmentSelector, BaseModal, OpenContractFilter },
    mixins: [restApi, timeUtils, priceHelper, invoicingHelper, attachmentHelper, downloadHelper],
    emits: ['close', 'submitted'],
    setup() {
        const showAlert = inject('showAlert')
        return { showAlert }
    },
    data() {
        return {
            selectedContract: null,
            fetchingContracts: false,
            contractAttachments: [],
            selectedAttachments: [],
            attachmentsError: false,
            loadingAttachments: false,
            validationErrors: {},
            row: { start_date: this.getDateAsString(this.getMonthStartAsDate()), end_date: '', info: '', unit_price: null },
            // Candidate lists from GET invoice/contractsanction/options. Each
            // can independently be empty (not configured for this company),
            // have exactly one entry (auto-selected, no picker shown), or
            // have 2+ entries (a picker is required and the id becomes
            // mandatory on submit).
            sanctionAccounts: [],
            sanctionCodes: [],
            selectedAccount: null,
            selectedCode: null,
            loadingOptions: false,
            optionsError: false,
            submitting: false,
            showConfirm: false
        }
    },
    computed: {
        // True while any background fetch (contracts, account/code
        // options, or contract attachments) is in flight. Drives the
        // full-editor loading spinner, same #loader/.spinner pattern
        // used elsewhere in the app (e.g. AccountEditor.vue,
        // ContractEditor.vue).
        loading() {
            return this.fetchingContracts || this.loadingOptions || this.loadingAttachments
        },

        total() {
            return this.row.unit_price ? Math.round(this.row.unit_price * 1 * 100) / 100 : 0
        },

        accountNeedsSelection() {
            return this.sanctionAccounts.length > 1
        },

        codeNeedsSelection() {
            return this.sanctionCodes.length > 1
        },

        singleAccount() {
            return this.sanctionAccounts.length === 1 ? this.sanctionAccounts[0] : null
        },

        singleCode() {
            return this.sanctionCodes.length === 1 ? this.sanctionCodes[0] : null
        },

        // Zero candidates for either account or code means the company is
        // not configured for Contract Sanctions at all - creation must be
        // blocked outright (per backend's "0 items -> creation will fail
        // with 400 regardless of input" note).
        creationBlocked() {
            return this.optionsError || this.sanctionAccounts.length === 0 || this.sanctionCodes.length === 0
        },

        isComplete() {
            return !!this.selectedContract &&
                !!this.row.start_date && !!this.row.end_date && this.row.end_date >= this.row.start_date &&
                this.row.unit_price !== null && this.row.unit_price !== undefined && this.row.unit_price !== '' && this.row.unit_price < 0 &&
                this.contractAttachments.length > 0 && this.selectedAttachments.length > 0 &&
                !this.creationBlocked &&
                (!this.accountNeedsSelection || !!this.selectedAccount) &&
                (!this.codeNeedsSelection || !!this.selectedCode)
        }
    },
    watch: {
        selectedContract(newContract, oldContract) {
            // Requirement 4.6: clear any previously selected attachments whenever
            // the selected contract changes.
            this.selectedAttachments = []
            this.contractAttachments = []
            this.attachmentsError = false

            if (newContract) {
                this.validationErrors.contract = null
                if (!oldContract || newContract.id !== oldContract.id) {
                    this.fetchContractAttachments(newContract)
                }
            }
        }
    },
    mounted() {
        this.fetchOptions()
    },
    methods: {
        setContract(contract) {
            this.selectedContract = contract
        },

        accountLabel(account) {
            if (!account) return ''
            return (account.code ? account.code + ' - ' : '') + (account.name || '')
        },

        codeLabel(code) {
            if (!code) return ''
            return (code.code ? code.code + ' - ' : '') + (code.name || '')
        },

        fetchOptions() {
            // GET invoice/contractsanction/options - returns the company's
            // candidate sanction accounts and invoicing codes (0, 1, or many
            // each, resolved independently).
            this.loadingOptions = true
            this.optionsError = false
            this.restFetchParams(this.contractSanctionOptionsUrl, {}, this.handleOptions, this.handleOptionsError)
        },

        handleOptions(response) {
            this.loadingOptions = false
            const data = (response && response.data) || {}
            this.sanctionAccounts = data.sanctionAccounts || data.sanction_accounts || []
            this.sanctionCodes = data.sanctionCodes || data.sanction_codes || []
            this.selectedAccount = this.sanctionAccounts.length === 1 ? this.sanctionAccounts[0] : null
            this.selectedCode = this.sanctionCodes.length === 1 ? this.sanctionCodes[0] : null
        },

        handleOptionsError() {
            this.loadingOptions = false
            this.sanctionAccounts = []
            this.sanctionCodes = []
            this.selectedAccount = null
            this.selectedCode = null
            this.optionsError = true
        },

        downloadAttachment(attachment) {
            this.loadingAttachments = true
            this.restFetch(
                this.contractAttachmentUrl + this.selectedContract.id + '/' + attachment.id,
                response => {
                    const fileName = attachment.file_original_name || attachment.filename || 'download'
                    this.saveFile(fileName, response.data)
                    this.loadingAttachments = false
                }, () => {
                    this.loadingAttachments = false
                })
        },

        fetchContractAttachments(contract) {
            // The contractOpenUrl list response does not reliably embed
            // attachments, so when they are missing, re-fetch the single
            // contract by id to get them (same pattern ContractDocuments.vue
            // uses via contractUrl + {id: ...}).
            if (Array.isArray(contract.attachments)) {
                this.contractAttachments = contract.attachments
                return
            }

            this.loadingAttachments = true
            this.attachmentsError = false
            this.restFetchParams(
                this.contractUrl,
                { id: contract.id },
                response => {
                    this.loadingAttachments = false
                    const fetched = response && response.data && response.data[0]
                    this.contractAttachments = (fetched && fetched.attachments) || []
                },
                () => {
                    this.loadingAttachments = false
                    this.contractAttachments = []
                    // Requirement 4.5: keep the selected contract so the user can retry.
                    this.attachmentsError = true
                }
            )
        },

        initiateSubmit() {
            this.validationErrors = {}

            if (!this.selectedContract) {
                this.validationErrors.contract = this.$t('contract_sanction.contract_required')
            }

            if (!this.row.start_date || !this.row.end_date || this.row.end_date < this.row.start_date) {
                this.validationErrors.timeRange = this.$t('contract_sanction.time_range_required')
            }

            if (this.row.unit_price === null || this.row.unit_price === undefined || this.row.unit_price === '') {
                this.validationErrors.unitPrice = this.$t('contract_sanction.unit_price_required')
            } else if (this.row.unit_price >= 0) {
                this.validationErrors.unitPrice = this.$t('contract_sanction.unit_price_must_be_negative')
            }

            if (this.selectedContract && (this.contractAttachments.length === 0 || this.selectedAttachments.length === 0)) {
                // Requirement 6.2 blocks submission while the selected contract has
                // zero attachments; Requirement 6.3 blocks submission when
                // attachments exist but none are selected.
                this.validationErrors.attachments = this.$t('contract_sanction.attachment_required')
            }

            if (this.sanctionAccounts.length === 0) {
                this.validationErrors.account = this.$t('contract_sanction.account_not_configured')
            } else if (this.accountNeedsSelection && !this.selectedAccount) {
                this.validationErrors.account = this.$t('contract_sanction.account_selection_required')
            }

            if (this.sanctionCodes.length === 0) {
                this.validationErrors.code = this.$t('contract_sanction.code_not_configured')
            } else if (this.codeNeedsSelection && !this.selectedCode) {
                this.validationErrors.code = this.$t('contract_sanction.code_selection_required')
            }

            if (this.isComplete) {
                this.showConfirm = true
            }
        },

        confirmSubmit() {
            this.submitting = true
            const payload = {
                contract_id: this.selectedContract.id,
                start_date: this.row.start_date,
                end_date: this.row.end_date,
                info: this.row.info,
                // Sent in cents (integer) - the UI displays/accepts
                // euros, but the backend stores/expects cents, same
                // convention as creditNoteUrl's credit_cost * 100.
                unit_price: Math.round(this.row.unit_price * 100),
                attachment_ids: this.selectedAttachments
            }
            // account/code are always sent when a value is selected,
            // whether it was user-picked (2+ candidates) or auto-selected
            // from a single candidate (selectedAccount/selectedCode is set
            // in handleOptions() in both cases). Sent as entity references
            // ({ id }) rather than flat account_id/code_id integers, since
            // the backend serializer resolves these into Account/ForestCode
            // entities.
            if (this.selectedAccount) {
                payload.account = { id: this.selectedAccount.id }
            }
            if (this.selectedCode) {
                payload.code = { id: this.selectedCode.id }
            }
            this.restAdd(this.contractSanctionUrl, payload, this.handleSubmitSuccess, this.handleSubmitError)
        },

        handleSubmitSuccess() {
            this.submitting = false
            this.showConfirm = false
            if (this.showAlert) {
                this.showAlert(this.$t('contract_sanction.success'), 'success')
            }
            this.$emit('submitted')
        },

        handleSubmitError(errorResponse) {
            this.submitting = false
            this.showConfirm = false
            // restAdd's failCallback receives the axios error response
            // directly (not a wrapped Error) - see RestApiMixin.js's
            // restAdd(). The backend returns a short plain-text message on
            // 400/403 (e.g. "Contract is not open", "Sanction account not
            // configured") - surface it directly when present, falling back
            // to the generic translated message otherwise.
            const backendMessage = errorResponse && typeof errorResponse.data === 'string'
                ? errorResponse.data
                : null
            if (this.showAlert) {
                this.showAlert(backendMessage || this.$t('contract_sanction.submit_error'))
            }
        }
    }
}
</script>

<style scoped>
/* Requirement 11.1: Bootstrap's col-sm-* grid classes used above
   (col-sm-6, col-sm-3) only auto-stack to full width below the 576px
   `sm` breakpoint. This feature targets the wider 768px breakpoint, so
   an explicit override is needed to force single-column rendering with
   no horizontal scrollbar up to 768px, consistent with Requirement 11.1. */
@media (max-width: 768px) {
    .row.g-2 > [class*="col-"] {
        flex: 0 0 100%;
        max-width: 100%;
    }
}


/* Custom header for the confirm modal (rendered with hide-header on
   base-modal) instead of using BaseModal's shared :title prop, so this
   long title wraps correctly at size="sm" without touching BaseModal's
   shared .modal-header style used by every other modal in the app. */
.contract-sanction-confirm-header {
    font-size: 1.6em;
    color: #0b4b8f;
    word-break: break-word;
    overflow-wrap: break-word;
}
/* ROUTA-5631: OpenContractFilter's root uses Bootstrap's .container class,
   which caps max-width and centers itself via margin: auto - unlike this
   editor's other full-width fields (time range, notes, account/code).
   Override it here only (Vue 3 scoped CSS applies this component's scope
   attribute to a directly-used child component's root element), so the
   contract dropdown spans the same width as its siblings without touching
   OpenContractFilter.vue itself. */
:deep(.container) {
    max-width: 100%;
    margin: 0;
}
</style>
